import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Empty, List, Modal, Space, Spin, Tag, Typography, message } from 'antd';
import { Link, useSearchParams, useParams } from 'react-router-dom';
import GroupedSegmentAudioPlayer from '../components/GroupedSegmentAudioPlayer';
import JobAudioPlayer from '../components/JobAudioPlayer';
import { useDevice } from '../contexts/DeviceContext';
import { API_CONFIG } from '../config/api';
import { secureLogger } from '../utils/secureLogger';
import { formatJstDateTime } from '../utils/dateTime';
import {
  getWhisperSegmentSummary,
  getWhisperTranscriptMessage,
  hasRecognizedSpeech,
  isNoSpeechDetected,
  isWhisperBatchEngine,
} from '../utils/whisperTranscriptState';
import { AudioSeries, AudioSeriesRetryResponse, TranscriptDetail, TranscriptSegment } from '../types/api';

const { Title, Text } = Typography;

const TranscriptJobDetailPage: React.FC = () => {
  const { deviceId, seriesId } = useParams<{ deviceId: string; seriesId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { devices, selectedDevice, setSelectedDevice } = useDevice();
  const [loading, setLoading] = useState(true);
  const [series, setSeries] = useState<AudioSeries | null>(null);
  const [deviceNotFound, setDeviceNotFound] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const selectedJobId = searchParams.get('job');

  const getAuthToken = useCallback(async (): Promise<string | null> => {
    const { fetchAuthSession } = await import('../auth/gateway');
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString() ?? null;
  }, []);

  useEffect(() => {
    if (!deviceId) {
      return;
    }
    if (devices.length === 0) {
      setDeviceNotFound(false);
      return;
    }
    const match = devices.find((device) => device.id === deviceId) || null;
    if (!match) {
      setDeviceNotFound(true);
      return;
    }
    setDeviceNotFound(false);
    if (selectedDevice?.id !== match.id) {
      setSelectedDevice(match);
    }
  }, [deviceId, devices, selectedDevice, setSelectedDevice]);

  const formatSeconds = useCallback((milliseconds: number) => `${(milliseconds / 1000).toFixed(1)}秒`, []);

  useEffect(() => {
    const load = async () => {
      if (!deviceId || !seriesId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const token = await getAuthToken();
        if (!token) {
          setSeries(null);
          return;
        }

        const query = selectedJobId ? `?jobId=${encodeURIComponent(selectedJobId)}` : '';
        const response = await fetch(
          `${API_CONFIG.BASE_URL}/api/v1/devices/${deviceId}/audio-series/${seriesId}${query}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (!response.ok) {
          secureLogger.error('Failed to load audio series detail', { deviceId, seriesId, selectedJobId, status: response.status });
          setSeries(null);
          return;
        }

        const data = await response.json() as AudioSeries;
        setSeries(data);
      } catch (error) {
        secureLogger.error('Audio series detail load failed', { deviceId, seriesId, selectedJobId, error });
        setSeries(null);
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [deviceId, getAuthToken, refreshNonce, selectedJobId, seriesId]);

  const segments = useMemo(() => {
    if (!series?.transcript?.segments) {
      return [];
    }
    return series.transcript.segments
      .filter((segment: TranscriptSegment) => segment.start_ms != null && segment.end_ms != null)
      .map((segment: TranscriptSegment, index) => ({
        key: `${segment.id ?? series.id}-${index}`,
        startSec: Math.max(0, (segment.start_ms ?? 0) / 1000),
        endSec: Math.max((segment.start_ms ?? 0) / 1000, (segment.end_ms ?? 0) / 1000),
        text: segment.text ?? '',
        speaker: segment.speaker_label ?? 'Unknown',
        confidence: segment.speaker_confidence ?? segment.segment_confidence,
        clipSlices: (segment.audio_slices ?? segment.slices ?? [])
          .filter((slice) => slice.start_offset_ms != null && slice.end_offset_ms != null)
          .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
          .map((slice) => ({
            audioId: slice.audio_id,
            startSec: Math.max(0, (slice.start_offset_ms ?? 0) / 1000),
            endSec: Math.max((slice.start_offset_ms ?? 0) / 1000, (slice.end_offset_ms ?? 0) / 1000),
          })),
      }));
  }, [series]);

  const formatClock = useCallback((seconds: number) => {
    if (!Number.isFinite(seconds)) {
      return '--:--';
    }
    const minutes = String(Math.floor(seconds / 60)).padStart(2, '0');
    const remain = String(Math.floor(seconds % 60)).padStart(2, '0');
    return `${minutes}:${remain}`;
  }, []);

  const renderFormatTag = useCallback((detail?: TranscriptDetail | null) => {
    const engine = detail?.engine;
    if (!engine) {
      return null;
    }
    return <Tag color={isWhisperBatchEngine(engine) ? 'purple' : 'default'}>{isWhisperBatchEngine(engine) ? '新形式' : '旧形式'}</Tag>;
  }, []);

  const retryTranscriptJob = useCallback(async () => {
    if (!deviceId || !seriesId) {
      return;
    }
    setRetrying(true);
    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('認証トークンを取得できませんでした');
      }

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/v1/devices/${deviceId}/audio-series/${seriesId}/retry`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        throw new Error(detail?.detail || `Retry failed (${response.status})`);
      }

      const payload = await response.json() as AudioSeriesRetryResponse;
      message.success(`新しいジョブ ${payload.new_job_id.slice(0, 8)} を再投入したよ`);
      setRefreshNonce((prev) => prev + 1);
    } catch (error) {
      secureLogger.error('Audio series retry failed', { deviceId, seriesId, error });
      message.error(error instanceof Error ? error.message : '音声認識ジョブの再投入に失敗しました');
    } finally {
      setRetrying(false);
    }
  }, [deviceId, getAuthToken, seriesId]);

  const confirmRetryTranscriptJob = useCallback(() => {
    Modal.confirm({
      title: '音声認識ジョブを再投入しますか？',
      content: '認識結果の履歴は残したまま、新しい job ID で Whisper バッチを再実行します。',
      okText: '再投入',
      cancelText: 'キャンセル',
      okButtonProps: { danger: true },
      onOk: retryTranscriptJob,
    });
  }, [retryTranscriptJob]);

  if (loading) {
    return <Spin size="large" />;
  }

  if (deviceNotFound) {
    return <Empty description="指定されたデバイスが見つかりません" />;
  }

  if (!series || !deviceId) {
    return <Empty description="audioSeries が見つかりません" />;
  }

  const transcript = series.transcript;
  const summary = getWhisperSegmentSummary(transcript);
  const summaryParts: string[] = [];
  if (summary?.segment_count) {
    summaryParts.push(`セグメント: ${summary.segment_count}件`);
  }
  if (summary?.duration_ms) {
    summaryParts.push(`合計 ${(summary.duration_ms / 1000).toFixed(1)}秒`);
  }
  if (summary?.language) {
    summaryParts.push(`言語: ${summary.language}`);
  }
  if (summary?.rtf) {
    summaryParts.push(`RTF: ${summary.rtf}`);
  }
  if (transcript && isNoSpeechDetected(transcript)) {
    summaryParts.push('認識区間なし');
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Space wrap>
            <Button>
              <Link to={`/devices/${deviceId}/audio`}>一覧へ戻る</Link>
            </Button>
            <Button
              type="primary"
              danger
              loading={retrying}
              onClick={confirmRetryTranscriptJob}
            >
              音声認識ジョブを再投入
            </Button>
            <Tag color="blue">Series {series.id.slice(0, 8)}</Tag>
            <Text code copyable={{ text: series.id }}>{series.id}</Text>
            <Tag color="blue">{formatSeconds(series.duration_ms)}</Tag>
            <Tag color="geekblue">{series.codec.toUpperCase()}</Tag>
            <Tag color="default">Run {series.run_count}</Tag>
            {series.latest_job_status && <Tag color="default">最新 {series.latest_job_status}</Tag>}
            {renderFormatTag(series.transcript)}
          </Space>
          <Title level={4} style={{ margin: 0 }}>
            {formatJstDateTime(series.started_at)}
          </Title>
          <JobAudioPlayer
            deviceId={deviceId}
            sources={series.source_audios.map((audio) => ({
              id: audio.id,
              durationMs: audio.duration_ms,
            }))}
          />
        </Space>
      </Card>

      <Card title={`構成音声 (${series.source_audios.length}件)`}>
        <List
          dataSource={series.source_audios}
          renderItem={(audio, index) => (
            <List.Item>
              <Space wrap>
                <Tag>#{index + 1}</Tag>
                <Text strong>{formatJstDateTime(audio.start_time)}</Text>
                <Text type="secondary">{formatSeconds(audio.duration_ms)}</Text>
                <Text code>{audio.id}</Text>
              </Space>
            </List.Item>
          )}
        />
      </Card>

      <Card title={`実行履歴 (${series.jobs?.length ?? 0}件)`}>
        <List
          dataSource={series.jobs || []}
          renderItem={(run) => (
            <List.Item
              actions={[
                <Button
                  key="select"
                  type={run.is_selected ? 'primary' : 'default'}
                  size="small"
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    if (run.id === series.current_job_id) {
                      next.delete('job');
                    } else {
                      next.set('job', run.id);
                    }
                    setSearchParams(next, { replace: false });
                  }}
                >
                  {run.is_selected ? '表示中' : 'この実行を表示'}
                </Button>,
              ]}
            >
              <Space wrap>
                <Tag color={run.is_selected ? 'blue' : 'default'}>{run.id.slice(0, 8)}</Tag>
                <Tag color={run.has_transcript ? 'green' : 'orange'}>{run.status}</Tag>
                <Text strong>{formatJstDateTime(run.created_at)}</Text>
                {run.transcript_created_at && (
                  <Text type="secondary">結果 {formatJstDateTime(run.transcript_created_at)}</Text>
                )}
                {run.engine && <Tag>{run.engine}</Tag>}
              </Space>
            </List.Item>
          )}
        />
      </Card>

      <Card title="音声認識結果">
        {!transcript ? (
          <Text type="secondary">音声認識結果なし</Text>
        ) : (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Text>
              {hasRecognizedSpeech(transcript)
                ? transcript.transcript_text
                : getWhisperTranscriptMessage(transcript)}
            </Text>
            {summaryParts.length > 0 && <Text type="secondary">{summaryParts.join(' / ')}</Text>}
            {segments.length > 0 && (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {segments.map((segment) => (
                  <div
                    key={segment.key}
                    style={{
                      border: '1px dashed #d9d9d9',
                      borderRadius: 8,
                      padding: 12,
                      background: '#fafafa',
                    }}
                  >
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Space wrap>
                        <Tag color="purple">{segment.speaker}</Tag>
                        <Text strong>
                          {formatClock(segment.startSec)} - {formatClock(segment.endSec)}
                        </Text>
                        {segment.confidence && (
                          <Text type="secondary">
                            conf {(segment.confidence * 100).toFixed(1)}%
                          </Text>
                        )}
                      </Space>
                      <Text>{segment.text || '...'}</Text>
                      {segment.clipSlices.length > 0 && (
                        <GroupedSegmentAudioPlayer
                          deviceId={deviceId}
                          clips={segment.clipSlices}
                          label="セグメント音声"
                        />
                      )}
                    </Space>
                  </div>
                ))}
              </Space>
            )}
          </Space>
        )}
      </Card>
    </Space>
  );
};

export default TranscriptJobDetailPage;
